/**
 * Art of Living Cookshop - Navigation Menu Interactions (v3)
 * Desktop (.aol-nav__desktop / .aol-mega-menu) and Mobile (.aol-mnav)
 * are now fully independent systems with separate markup and separate
 * event handling. Neither shares elements with the other.
 */

class AOLNavigation {
  constructor() {
    this.nav = document.querySelector('.aol-nav');
    this.activeMenu = null;

    this.init();
  }

  init() {
    this.setupDesktopMenus();
    this.setupDesktopAccessibility();
    this.setupMobileNav();

    document.addEventListener('click', (e) => this.handleOutsideClick(e));
  }

  // ============================================================
  // DESKTOP MEGA MENU (hover-activated only)
  // ============================================================

  setupDesktopMenus() {
    const menuButtons = this.nav.querySelectorAll('.aol-nav__desktop [data-menu]');
    let hideTimeout;

    menuButtons.forEach((button) => {
      const menuId = button.getAttribute('data-menu');
      const menuElement = document.querySelector(`.aol-mega-menu[data-menu="${menuId}"]`);
      if (!menuElement) return;

      button.addEventListener('mouseenter', () => {
        clearTimeout(hideTimeout);
        menuButtons.forEach((btn) => {
          if (btn !== button && btn.getAttribute('aria-expanded') === 'true') {
            this.closeMegaMenuForButton(btn);
          }
        });
        this.openMegaMenu(button);
      });

      button.addEventListener('mouseleave', () => {
        hideTimeout = setTimeout(() => this.closeMegaMenu(), 100);
      });

      menuElement.addEventListener('mouseenter', () => clearTimeout(hideTimeout));
      menuElement.addEventListener('mouseleave', () => {
        hideTimeout = setTimeout(() => this.closeMegaMenu(), 100);
      });

      menuElement.querySelectorAll('a').forEach((link) => {
        link.addEventListener('click', (e) => {
          e.stopPropagation();
          this.closeMegaMenu();
        });
      });
    });
  }

  openMegaMenu(button) {
    const menuId = button.getAttribute('data-menu');
    const menuElement = document.querySelector(`.aol-mega-menu[data-menu="${menuId}"]`);
    button.setAttribute('aria-expanded', 'true');
    if (menuElement) {
      menuElement.classList.add('open');
      menuElement.removeAttribute('hidden');
    }
    this.activeMenu = button;
  }

  closeMegaMenu() {
    if (this.activeMenu) {
      this.closeMegaMenuForButton(this.activeMenu);
      this.activeMenu = null;
    }
  }

  closeMegaMenuForButton(button) {
    const menuId = button.getAttribute('data-menu');
    const menuElement = document.querySelector(`.aol-mega-menu[data-menu="${menuId}"]`);
    button.setAttribute('aria-expanded', 'false');
    if (menuElement) {
      menuElement.classList.remove('open');
      menuElement.setAttribute('hidden', '');
    }
  }

  setupDesktopAccessibility() {
    const menuButtons = this.nav.querySelectorAll('.aol-nav__desktop [data-menu]');

    menuButtons.forEach((button, index) => {
      button.addEventListener('keydown', (e) => {
        switch (e.key) {
          case 'Enter':
          case ' ':
            e.preventDefault();
            if (button.getAttribute('aria-expanded') === 'true') {
              this.closeMegaMenu();
            } else {
              menuButtons.forEach((btn) => {
                if (btn !== button && btn.getAttribute('aria-expanded') === 'true') {
                  this.closeMegaMenuForButton(btn);
                }
              });
              this.openMegaMenu(button);
            }
            break;
          case 'Escape':
            this.closeMegaMenu();
            break;
          case 'ArrowRight':
            e.preventDefault();
            if (index < menuButtons.length - 1) menuButtons[index + 1].focus();
            break;
          case 'ArrowLeft':
            e.preventDefault();
            if (index > 0) menuButtons[index - 1].focus();
            break;
        }
      });
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.closeMegaMenu();
    });
  }

  // ============================================================
  // MOBILE NAV (tap-activated only, completely separate markup)
  // ============================================================

  setupMobileNav() {
    const toggle = document.querySelector('.aol-nav__mobile-toggle');
    const overlay = document.querySelector('.aol-mnav__overlay');
    const mnav = document.getElementById('aol-mnav');
    if (!toggle || !overlay || !mnav) return;

    toggle.addEventListener('click', () => {
      const isOpen = mnav.classList.contains('aol-mnav--open');
      isOpen ? this.closeMobileNav() : this.openMobileNav();
    });

    overlay.addEventListener('click', () => this.closeMobileNav());

    // Top-level accordion (Cookware, Kitchen Tools, etc)
    const toplevelButtons = mnav.querySelectorAll('.aol-mnav__toplevel-link');
    toplevelButtons.forEach((button) => {
      const panel = button.nextElementSibling;
      button.addEventListener('click', () => {
        const isOpen = button.getAttribute('aria-expanded') === 'true';
        toplevelButtons.forEach((btn) => {
          if (btn !== button) {
            btn.setAttribute('aria-expanded', 'false');
            const otherPanel = btn.nextElementSibling;
            if (otherPanel) otherPanel.setAttribute('hidden', '');
          }
        });
        button.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
        if (panel) {
          if (isOpen) {
            panel.setAttribute('hidden', '');
          } else {
            panel.removeAttribute('hidden');
          }
        }
      });
    });

    // Second-level accordion (Shop by Type, etc) within each panel.
    // A panel with only one heading auto-shows its list with no toggle.
    mnav.querySelectorAll('.aol-mnav__panel-content').forEach((panel) => {
      const headings = panel.querySelectorAll('.aol-mnav__heading');

      headings.forEach((heading) => {
        const list = heading.nextElementSibling;
        if (!list || !list.classList.contains('aol-mnav__list')) return;

        if (headings.length <= 1) {
          heading.classList.add('aol-mnav__heading--static');
          list.classList.add('aol-mnav__list--open');
          return;
        }

        heading.setAttribute('role', 'button');
        heading.setAttribute('tabindex', '0');
        heading.setAttribute('aria-expanded', 'false');

        const toggleThis = () => {
          const isOpen = heading.getAttribute('aria-expanded') === 'true';
          heading.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
          list.classList.toggle('aol-mnav__list--open', !isOpen);
        };

        heading.addEventListener('click', toggleThis);
        heading.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleThis();
          }
        });
      });
    });

    // Close the whole mobile nav when any link inside it is tapped
    mnav.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', () => this.closeMobileNav());
    });
  }

  openMobileNav() {
    this.moveMobileNavToBody();
    this.positionMobileNav();
    document.getElementById('aol-mnav')?.classList.add('aol-mnav--open');
    document.querySelector('.aol-mnav__overlay')?.removeAttribute('hidden');
    document.getElementById('aol-mnav')?.removeAttribute('hidden');
    document.querySelector('.aol-nav__mobile-toggle')?.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
  }

  closeMobileNav() {
    document.getElementById('aol-mnav')?.classList.remove('aol-mnav--open');
    document.querySelector('.aol-mnav__overlay')?.setAttribute('hidden', '');
    document.querySelector('.aol-nav__mobile-toggle')?.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    this.restoreMobileNavPosition();
  }

  // Moves the mobile nav to <body> when opened, so it escapes the
  // stacking context created by .aol-nav (position: sticky + z-index),
  // which would otherwise trap it behind the header's search row.
  moveMobileNavToBody() {
    const mnav = document.getElementById('aol-mnav');
    const overlay = document.querySelector('.aol-mnav__overlay');
    if (!mnav || !overlay) return;

    if (!this.mnavOriginalParent) {
      this.mnavOriginalParent = mnav.parentElement;
      this.mnavNextSibling = mnav.nextSibling;
      this.overlayOriginalParent = overlay.parentElement;
      this.overlayNextSibling = overlay.nextSibling;
    }

    document.body.appendChild(overlay);
    document.body.appendChild(mnav);
  }

  restoreMobileNavPosition() {
    const mnav = document.getElementById('aol-mnav');
    const overlay = document.querySelector('.aol-mnav__overlay');

    if (this.mnavOriginalParent && mnav) {
      this.mnavOriginalParent.insertBefore(mnav, this.mnavNextSibling);
    }
    if (this.overlayOriginalParent && overlay) {
      this.overlayOriginalParent.insertBefore(overlay, this.overlayNextSibling);
    }
  }

  // Positions the mobile nav directly below the header's search row,
  // recalculated fresh each time it opens (header height can vary,
  // e.g. if the promo banner above it has been dismissed).
  positionMobileNav() {
    const searchSub = document.querySelector('.site-header__element--sub[data-type="search"]');
    const header = document.getElementById('SiteHeader');
    const anchor = searchSub || header;
    const mnav = document.getElementById('aol-mnav');
    const overlay = document.querySelector('.aol-mnav__overlay');
    if (!anchor || !mnav) return;

    const anchorBottom = anchor.getBoundingClientRect().bottom;
    mnav.style.top = `${anchorBottom}px`;
    mnav.style.maxHeight = `calc(100vh - ${anchorBottom}px)`;
    if (overlay) overlay.style.top = `${anchorBottom}px`;
  }

  // ============================================================
  // UTILITY
  // ============================================================

  handleOutsideClick(event) {
    const mnav = document.getElementById('aol-mnav');

    // Desktop mega menu outside-click handling
    if (!this.nav.contains(event.target)) {
      const megaMenus = document.querySelectorAll('.aol-mega-menu');
      let clickedInMenu = false;
      megaMenus.forEach((menu) => {
        if (menu.contains(event.target)) clickedInMenu = true;
      });
      if (!clickedInMenu) this.closeMegaMenu();
    }

    // Mobile nav outside-click handling (mnav may be portaled to <body>,
    // so this.nav.contains() won't find it - check it separately)
    if (mnav && mnav.classList.contains('aol-mnav--open') && !mnav.contains(event.target)) {
      const toggle = document.querySelector('.aol-nav__mobile-toggle');
      if (!toggle || !toggle.contains(event.target)) {
        this.closeMobileNav();
      }
    }
  }
}

// Initialize navigation when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new AOLNavigation();
  });
} else {
  new AOLNavigation();
}